import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import TableContainer from '@mui/material/TableContainer';
import { getPaginationLabels } from '@components/Table';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import ActionIconButton from '@components/ActionIconButton';
import { keywordsActions } from '@actions';
import { useTranslation } from '@hooks';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { InsightsCard, StyledTable, SentimentWrapper, SentimentPiece } from './styles';

const getTableHeader = t => {
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('AnalyticsKeywords.keyword')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsKeywords.avgRating')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsKeywords.numberOfReviews')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('AnalyticsKeywords.sentiment')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const getTableBody = (data, onClickKeyword, t) => {
    return data.map(keyword => {
        const positive = keyword.sentiments.Positive || 0;
        const neutral = keyword.sentiments.Neutral || 0;
        const negative = keyword.sentiments.Negative || 0;
        const total = positive + neutral + negative;
        const positivePercent = (positive * 100) / total;
        const neutralPercent = (neutral * 100) / total;
        const negativePercent = (negative * 100) / total;

        const positiveTooltip = positive > 0 ? `${t('FeedbackSurveys.positive')}: ${positive}` : '';
        const neutralTooltip = neutral > 0 ? `${t('FeedbackSurveys.neutral')}: ${neutral}` : '';
        const negativeTooltip = negative > 0 ? `${t('FeedbackSurveys.negative')}: ${negative}` : '';

        const tooltipText = [positiveTooltip, neutralTooltip, negativeTooltip].filter(Boolean).join(', ');

        return [
            {
                component: (
                    <Typography variant="subtitle2" align="left">
                        {keyword.keyword}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="right">
                        {keyword.averageRating.toFixed(2)}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="right">
                        {keyword.numberOfReviews}
                    </Typography>
                ),
            },
            {
                component: (
                    <Tooltip title={tooltipText} placement="top">
                        <SentimentWrapper>
                            <SentimentPiece type={1} percentage={positivePercent} />
                            <SentimentPiece type={0} percentage={neutralPercent} />
                            <SentimentPiece type={2} percentage={negativePercent} />
                        </SentimentWrapper>
                    </Tooltip>
                ),
            },
            {
                component: (
                    <ActionIconButton title={t('common.view')} onClick={() => onClickKeyword(keyword.keyword)}>
                        <VisibilityIcon />
                    </ActionIconButton>
                ),
                value: true,
            },
        ];
    });
};

const KeywordsTableCard = ({ currentQuery, hasFilters, filterVersion, onClickKeyword }) => {
    const { t } = useTranslation();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

    const { data, loading, totalCount, pages } = useSelector(state => state.keywords);

    const isMobile = useMediaQuery('@media (max-width: 1024px)');
    const dispatch = useDispatch();
    const lastFetchedVersion = useRef(-1);

    useEffect(() => {
        if (filterVersion > lastFetchedVersion.current && hasFilters) {
            dispatch(keywordsActions.get(currentQuery, 0, rowsPerPage));
            setPage(0);
            lastFetchedVersion.current = filterVersion;
        }
    }, [filterVersion, currentQuery]);

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(keywordsActions.get(currentQuery, newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(keywordsActions.get(currentQuery, 0, parseInt(event.target.value, 10)));
    };

    return (
        <InsightsCard>
            <Typography variant="title1" color="textPrimary" style={{ marginBottom: '16px', display: 'block' }}>
                {t('AnalyticsKeywords.tableTitle')}
            </Typography>
            <TableContainer>
                <StyledTable
                    withHover
                    columnNames={getTableHeader(t)}
                    data={data}
                    rows={getTableBody(data, onClickKeyword, t)}
                    onRowClick={keyword => onClickKeyword(keyword.keyword)}
                    isDataLoading={loading}
                    withPagination
                    isInteractive={!isMobile}
                    isCustomActionComponent
                    paginationProps={{
                        colSpan: 5,
                        count: totalCount,
                        rowsPerPage,
                        page,
                        onPageChange,
                        onRowsPerPageChange,
                        ...getPaginationLabels(t),
                        pages,
                    }}
                />
            </TableContainer>
        </InsightsCard>
    );
};

export default KeywordsTableCard;
